<template>
  <div class="landing-page">
    <section class="hero">
      <div class="hero-glow hero-glow--one"></div>
      <div class="hero-glow hero-glow--two"></div>

      <header class="topbar">
        <router-link to="/" class="brand" aria-label="ubuHle home">
          <img src="/logo.png" alt="ubuHle" class="brand-mark" />
        </router-link>

        <nav class="topbar-actions" aria-label="Primary navigation">
          <a href="#features">Features</a>
          <a href="#how-it-works">How it works</a>
          <a href="#business">Business</a>
          <router-link to="/login" class="ghost-link">Sign in</router-link>
        </nav>
      </header>

      <div class="hero-grid">
        <div class="hero-copy">
          <p class="eyebrow">Book beauty appointments faster</p>
          <h1>Turn walk-ins, queues, and appointments into one seamless experience.</h1>
          <p class="hero-text">
            ubuHle helps customers discover salons, book services, and join queues without the friction.
            For salon owners, it brings bookings, staff, and live queue control into a single workflow.
          </p>

          <div class="hero-actions">
            <a href="https://apps.apple.com/za/app/ubuhle/id6737440531" target="_blank" rel="noreferrer" class="primary-link">
              Download the app
            </a>
            <router-link to="/register" class="secondary-link">Join as a salon owner</router-link>
          </div>
        </div>

        <div class="hero-showcase">
          <div class="phone-stack phone-stack--main">
            <img src="/hero-join-queue-page.png" alt="ubuHle mobile home screen" />
          </div>
        </div>
      </div>
    </section>

    <section id="features" class="feature-strip">
      <article>
        <span class="feature-icon"><i class="bi bi-search"></i></span>
        <h3>Find nearby salons</h3>
        <p>Help customers discover the right place quickly with browsing and location-aware discovery.</p>
      </article>
      <article>
        <span class="feature-icon"><i class="bi bi-calendar-check"></i></span>
        <h3>Book in advance</h3>
        <p>Reduce no-shows and manual admin with a simple appointment flow that feels effortless.</p>
      </article>
      <article>
        <span class="feature-icon"><i class="bi bi-people"></i></span>
        <h3>Manage queues</h3>
        <p>Keep customer flow organized with live queue handling and service status updates.</p>
      </article>
    </section>

    <section id="how-it-works" class="section-block">
      <div class="section-heading">
        <p class="eyebrow">How it works</p>
        <h2>A simple booking flow that customers understand immediately.</h2>
      </div>

      <div class="steps-grid">
        <div class="step-card">
          <img src="/salon-selection-app-page.png" alt="Choose a salon" />
          <div>
            <span>01</span>
            <h3>Choose your salon</h3>
            <p>Browse options and pick the venue that fits the service you need.</p>
          </div>
        </div>
        <div class="step-card">
          <img src="/service-selection-app-page.png" alt="Select a service" />
          <div>
            <span>02</span>
            <h3>Select a service</h3>
            <p>Review the available services and move straight into booking.</p>
          </div>
        </div>
        <div class="step-card">
          <img src="/choosing-slot-app-page.png" alt="Choose a booking slot" />
          <div>
            <span>03</span>
            <h3>Pick a time slot</h3>
            <p>Choose a time and professional that work for you without back-and-forth.</p>
          </div>
        </div>
        <div class="step-card">
          <img src="/appointment-summary-app-page.png" alt="Confirm a booking" />
          <div>
            <span>04</span>
            <h3>Confirm the booking</h3>
            <p>Review the details and confirm the appointment instantly.</p>
          </div>
        </div>
      </div>
    </section>

    <section class="feature-spotlight">
      <div class="spotlight-copy">
        <p class="eyebrow">Experience the app</p>
        <h2>Customers get clarity. Salon owners get control.</h2>
        <p>
          The product is designed to handle both sides of the experience: a polished customer journey on mobile
          and practical operational tools for the business.
        </p>

        <div class="spotlight-cards">
          <div>
            <strong>Bookings</strong>
            <span>Track upcoming appointments and history in one place.</span>
          </div>
          <div>
            <strong>Live queue</strong>
            <span>See what is happening now and keep customers moving.</span>
          </div>
          <div>
            <strong>Services</strong>
            <span>Manage the catalog and keep offerings up to date.</span>
          </div>
        </div>
      </div>

      <div class="spotlight-media">
        <img src="/bookings-app-page.png" alt="Mobile bookings screen" />
        <img src="/service-queue-selection-app-page.png" alt="Queue selection screen" class="overlap" />
      </div>
    </section>

    <section id="business" class="business-band">
      <div>
        <p class="eyebrow">Grow your beauty business</p>
        <h2>Designed to increase bookings, improve visibility, and simplify salon operations.</h2>
      </div>
      <div class="business-metrics">
        <div><strong>Bookings</strong><span>Manage every request from a clean dashboard.</span></div>
        <div><strong>Queue flow</strong><span>Reduce waiting-room uncertainty for customers.</span></div>
        <div><strong>Analytics</strong><span>Keep an eye on what matters without extra admin.</span></div>
      </div>
    </section>

    <section class="cta-band">
      <div>
        <p class="eyebrow">Ready to launch</p>
        <h2>Download ubuHle or register your salon to get started.</h2>
      </div>
      <div class="cta-actions">
        <a href="https://apps.apple.com/za/app/ubuhle/id6737440531" target="_blank" rel="noreferrer" class="primary-link">
          Download on App Store
        </a>
        <router-link to="/register" class="secondary-link">Register your salon</router-link>
      </div>
    </section>

    <footer class="landing-footer">
      <div>
        <strong>ubuHle</strong>
        <p>Connecting beauty professionals with customers across South Africa.</p>
      </div>
      <nav aria-label="Footer legal links" class="landing-footer-links">
        <router-link to="/legal/privacy-policy">Privacy Policy</router-link>
        <router-link to="/legal/contact-us">Contact Us</router-link>
      </nav>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'LandingPage'
}
</script>

<style scoped>
.landing-page {
  --landing-bg: #0b0b0c;
  --landing-surface: rgba(255, 255, 255, 0.06);
  --landing-surface-strong: rgba(255, 255, 255, 0.1);
  --landing-text: #f5f2eb;
  --landing-muted: rgba(245, 242, 235, 0.72);
  --landing-accent: #ff9d00;
  --landing-accent-soft: rgba(255, 157, 0, 0.18);
  --landing-border: rgba(255, 255, 255, 0.12);
  min-height: 100vh;
  color: var(--landing-text);
  background:
    radial-gradient(circle at top left, rgba(255, 157, 0, 0.18), transparent 35%),
    radial-gradient(circle at 85% 15%, rgba(255, 255, 255, 0.08), transparent 24%),
    linear-gradient(180deg, #111113 0%, #09090a 100%);
}

.hero {
  position: relative;
  overflow: hidden;
  padding: 28px clamp(20px, 4vw, 56px) 40px;
}

.hero-glow {
  position: absolute;
  border-radius: 999px;
  filter: blur(30px);
  pointer-events: none;
}

.hero-glow--one {
  width: 260px;
  height: 260px;
  top: 60px;
  left: -80px;
  background: rgba(255, 157, 0, 0.16);
}

.hero-glow--two {
  width: 320px;
  height: 320px;
  right: -120px;
  top: 110px;
  background: rgba(255, 255, 255, 0.08);
}

.topbar,
.hero-grid,
.feature-strip,
.section-block,
.feature-spotlight,
.business-band,
.cta-band {
  position: relative;
  z-index: 1;
}

.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin: 0 auto 64px;
  max-width: 1240px;
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 14px;
  color: inherit;
  text-decoration: none;
}

.brand-mark {
  height: 46px;
  object-fit: cover;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.24);
}

.brand-name {
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.brand-tag {
  font-size: 0.78rem;
  color: var(--landing-muted);
}

.topbar-actions {
  display: flex;
  align-items: center;
  gap: 22px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.topbar-actions a {
  color: var(--landing-muted);
  text-decoration: none;
  font-weight: 600;
}

.topbar-actions a:hover {
  color: var(--landing-text);
}

.ghost-link {
  padding: 12px 16px;
  border: 1px solid var(--landing-border);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.03);
}

.hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(420px, 0.9fr);
  gap: 44px;
  align-items: center;
  max-width: 1240px;
  margin: 0 auto;
}

.hero-copy h1,
.section-heading h2,
.spotlight-copy h2,
.business-band h2,
.cta-band h2 {
  margin: 0;
  line-height: 0.98;
  letter-spacing: -0.04em;
}

.hero-copy h1 {
  font-size: clamp(3rem, 6vw, 5.5rem);
  max-width: 10ch;
}

.eyebrow {
  margin: 0 0 18px;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--landing-accent);
  font-size: 0.74rem;
  font-weight: 800;
}

.hero-text,
.section-heading p,
.spotlight-copy p,
.business-band p,
.cta-band p {
  color: var(--landing-muted);
  font-size: 1.02rem;
  line-height: 1.75;
}

.hero-text {
  max-width: 60ch;
  margin: 24px 0 0;
}

.hero-actions,
.cta-actions {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 30px;
}

.primary-link,
.secondary-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 20px;
  border-radius: 999px;
  text-decoration: none;
  font-weight: 700;
}

.primary-link {
  background: linear-gradient(135deg, #ffb200 0%, #ff8f00 100%);
  color: #1b1304;
  box-shadow: 0 16px 34px rgba(255, 157, 0, 0.26);
}

.secondary-link {
  color: var(--landing-text);
  border: 1px solid var(--landing-border);
  background: rgba(255, 255, 255, 0.03);
}

.hero-points {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 34px;
}

.hero-points > div,
.feature-strip article,
.step-card,
.spotlight-cards > div,
.business-metrics > div {
  border: 1px solid var(--landing-border);
  background: var(--landing-surface);
  backdrop-filter: blur(18px);
}

.hero-points > div {
  border-radius: 22px;
  padding: 18px;
}

.hero-points strong,
.spotlight-cards strong,
.business-metrics strong {
  display: block;
  font-size: 0.95rem;
  margin-bottom: 8px;
}

.hero-points span,
.spotlight-cards span,
.business-metrics span,
.step-card p {
  color: var(--landing-muted);
  font-size: 0.92rem;
  line-height: 1.55;
}

.hero-showcase {
  position: relative;
  min-height: 780px;
}

.phone-stack {
  position: absolute;
  border-radius: 34px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.38);
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #000;
}

.phone-stack img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.phone-stack--main {
  inset: 0 10px 0 110px;
}

.phone-stack--small {
  width: 37%;
  aspect-ratio: 0.68;
  bottom: 28px;
}

.phone-stack--left {
  left: 0;
  transform: rotate(-6deg);
  z-index: 2;
}

.phone-stack--right {
  right: 0;
  transform: translateY(-26px) rotate(7deg);
  z-index: 3;
}

.feature-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
  max-width: 1240px;
  margin: 28px auto 0;
  padding: 0 clamp(20px, 4vw, 56px);
}

.feature-strip article {
  border-radius: 24px;
  padding: 24px;
}

.feature-icon {
  display: inline-flex;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: var(--landing-accent-soft);
  color: var(--landing-accent);
  font-size: 1.1rem;
  margin-bottom: 18px;
}

.feature-strip h3,
.step-card h3,
.spotlight-copy h2,
.business-band h2,
.cta-band h2 {
  font-size: clamp(1.6rem, 2.2vw, 2.4rem);
}

.feature-strip h3 {
  margin: 0 0 10px;
}

.section-block,
.feature-spotlight,
.business-band,
.cta-band {
  max-width: 1240px;
  margin: 0 auto;
  padding: 92px clamp(20px, 4vw, 56px) 0;
}

.section-heading {
  max-width: 780px;
  margin-bottom: 30px;
}

.section-heading h2,
.spotlight-copy h2,
.business-band h2,
.cta-band h2 {
  font-size: clamp(2rem, 4vw, 3.5rem);
  max-width: 14ch;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}

.step-card {
  border-radius: 26px;
  overflow: hidden;
}

.step-card img {
  display: block;
  width: 100%;
  aspect-ratio: 0;
  object-fit: cover;
}

.step-card div {
  padding: 20px;
}

.step-card span {
  display: inline-block;
  color: var(--landing-accent);
  font-weight: 800;
  letter-spacing: 0.12em;
  margin-bottom: 12px;
}

.step-card h3 {
  margin: 0 0 10px;
  font-size: 1.2rem;
}

.feature-spotlight {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(360px, 0.95fr);
  gap: 28px;
  align-items: center;
}

.spotlight-cards,
.business-metrics {
  display: grid;
  gap: 14px;
  margin-top: 26px;
}

.spotlight-cards > div,
.business-metrics > div {
  border-radius: 22px;
  padding: 18px 20px;
}

.spotlight-media {
  position: relative;
  min-height: 540px;
}

.spotlight-media img {
  position: absolute;
  width: min(320px, 78%);
  border-radius: 30px;
  box-shadow: 0 22px 50px rgba(0, 0, 0, 0.32);
}

.spotlight-media img:first-child {
  left: 0;
  top: 28px;
}

.spotlight-media img.overlap {
  right: 0;
  bottom: 10px;
}

.business-band {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 24px;
  align-items: start;
}

.business-band h2,
.cta-band h2 {
  max-width: 15ch;
}

.business-metrics {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.cta-band {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding-bottom: 72px;
}

.landing-footer {
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 clamp(20px, 4vw, 56px) 42px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  color: var(--landing-muted);
}

.landing-footer strong {
  display: block;
  color: var(--landing-text);
  font-size: 0.98rem;
  margin-bottom: 6px;
}

.landing-footer p {
  margin: 0;
  line-height: 1.6;
}

.landing-footer-links {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.landing-footer-links a {
  color: var(--landing-text);
  text-decoration: none;
  font-weight: 700;
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid var(--landing-border);
  background: rgba(255, 255, 255, 0.03);
}

.landing-footer-links a:hover {
  background: rgba(255, 255, 255, 0.08);
}

@media (max-width: 1180px) {
  .hero-grid,
  .feature-spotlight,
  .business-band,
  .cta-band {
    grid-template-columns: 1fr;
    display: grid;
  }

  .hero-showcase {
    min-height: 680px;
    max-width: 760px;
    margin: 0 auto;
  }

  .steps-grid,
  .feature-strip,
  .business-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .topbar {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 44px;
  }

  .topbar-actions {
    justify-content: flex-start;
    gap: 14px;
  }

  .hero-grid,
  .feature-strip,
  .steps-grid,
  .business-metrics {
    grid-template-columns: 1fr;
  }

  .hero-copy h1 {
    max-width: none;
  }

  .hero-points {
    grid-template-columns: 1fr;
  }

  .hero-showcase {
    min-height: 560px;
  }

  .phone-stack--main {
    inset: 0 14px 0 86px;
  }

  .phone-stack--small {
    width: 42%;
  }

  .section-block,
  .feature-spotlight,
  .business-band,
  .cta-band {
    padding-top: 72px;
  }

  .cta-band {
    justify-content: flex-start;
  }

  .landing-footer {
    align-items: flex-start;
  }
}
</style>